import { AxiosResponse } from "axios";
import axios from "./axios";
import { Certificate, UserInfo } from "../types";

type ApiResponse<T> = {
  data: T;
};

export const updateEntity = async (entityId: number, body: any) => {
  try {
    const response = await axios.put(`/football-entities/${entityId}`, body);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch profile data");
  }
};

export const fetchFootballEntities = async () => {
  try {
    const response = await axios.get(`/football-entities/explore`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entities data");
  }
};

export const fetchEntityProfileInfo = async () => {
  try {
    const response = await axios.get(`/users/profile/v1`);
    localStorage.setItem("entityId", response?.data?.data?.entity?.id);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch profile data");
  }
};

export const getEntityProfile = async (id: string) => {
  try {
    const response = await axios.get(`football-entities/${id}/`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity data");
  }
};

export const getFollowersCountPerAge = async (id: string) => {
  try {
    const response = await axios.get(`football-entities/${id}/followers-count-per-age`);

    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity followers");
  }
};

export const getEntityStatistics = async (id: string) => {
  try {
    const response = await axios.get(`football-entities/${id}/dashboard`);

    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity statistics");
  }
};

export const getEntityFollowers = async (id: string, daysQuery: string) => {
  const QUERY = daysQuery === "ALL_TIME" ? "" : `?days=${daysQuery.toLocaleLowerCase()}`;

  try {
    const response = await axios.get(`football-entities/${id}/followers-count-per-day${QUERY}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity statistics");
  }
};

// Get entity details
export const getEntityDetails = async (userId: string): Promise<UserInfo> => {
  try {
    const response: AxiosResponse<ApiResponse<UserInfo>> = await axios.get(`users/${userId}/v1`);
    return response.data.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity details: ${(err as Error).message}`);
  }
};

// Get entity certificates
export const getEntityCertificates = async (userId: string): Promise<Certificate[]> => {
  try {
    const response: AxiosResponse<ApiResponse<Certificate[]>> = await axios.get(`users/${userId}/certificates/v1`);
    return response.data.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity certificates: ${(err as Error).message}`);
  }
};

interface EndorsedSkill {
  id: number;
  title: string;
  count: string;
  percentage: number | null;
}

interface SkillsResponse {
  data: EndorsedSkill[];
  count: number;
}

export const getEntitySkills = async (userId: string): Promise<SkillsResponse> => {
  try {
    const response: AxiosResponse<SkillsResponse> = await axios.get(`users/${userId}/skills/v1`);
    return response.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity skills: ${(err as Error).message}`);
  }
};

export const followUser = async (userId: string) => {
  try {
    const response = await axios.patch(`users/${userId}/follow`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to follow user");
  }
};

export const unFollowUser = async (userId: string) => {
  try {
    const response = await axios.delete(`users/${userId}/follow`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to follow user");
  }
};

export const followEntity = async (entityId: string) => {
  try {
    const response = await axios.put(`football-entities/${entityId}/follow`);
    return response.data.isFollowing;
  } catch (err) {
    throw new Error(err + " Failed to follow entity");
  }
};

export const unFollowEntity = async (entityId: string) => {
  try {
    const response = await axios.delete(`football-entities/${entityId}/follow`);
    return response.data.isFollowing;
  } catch (err) {
    throw new Error(err + " Failed to follow entity");
  }
};
